import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Bell, Search, User, Home, FileText, Settings, Users, Package, Building, Plus, CreditCard, LineChart, BarChart4, DollarSign, Phone, Calendar, ReceiptText, Briefcase, PenSquare, BarChart3, Sparkles, Box, Warehouse, Factory, ShoppingCart, Truck, ClipboardList } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import Favicon from "../../public/images/favicon.png";
import { useBrand } from '@/lib/brand';

import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { canView, canCreate, validatePermissions, initializeMissingModules } from '@/lib/permissions';

// Define types for navigation
type SubNavigationItem = {
  name: string;
  href: string;
  current: boolean;
  comingSoon?: boolean;
  moduleName?: string;
};

type NavigationItem = {
  name: string;
  href: string;
  icon: LucideIcon;
  current: boolean;
  comingSoon?: boolean;
  moduleName?: string;
  subNavigation?: SubNavigationItem[];
};

type ViewType = 'dashboard' | 'accounting' | 'sales' | 'crm' | 'inventory' | 'manufacturing' | 'procurement';

// Define available views with colors
const views = [
  { id: 'dashboard', name: 'Dashboard', icon: Home, color: 'bg-slate-100 hover:bg-slate-200 text-slate-800', moduleName: 'Dashboard' },
  { id: 'accounting', name: 'Accounting', icon: DollarSign, color: 'bg-blue-100 hover:bg-blue-200 text-blue-800', moduleName: 'Accounting' },
  { id: 'sales', name: 'Sales', icon: LineChart, color: 'bg-green-100 hover:bg-green-200 text-green-800', moduleName: 'Sales' },
  { id: 'crm', name: 'CRM', icon: Users, color: 'bg-purple-100 hover:bg-purple-200 text-purple-800', moduleName: 'CRM' },
  { id: 'inventory', name: 'Inventory', icon: Warehouse, color: 'bg-orange-100 hover:bg-orange-200 text-orange-800', moduleName: 'Inventory' },
  { id: 'manufacturing', name: 'Manufacturing', icon: Factory, color: 'bg-indigo-100 hover:bg-indigo-200 text-indigo-800', moduleName: 'Manufacturing' },
  { id: 'procurement', name: 'Procurement', icon: ShoppingCart, color: 'bg-teal-100 hover:bg-teal-200 text-teal-800', moduleName: 'Procurement' },
];

// View-specific navigation
const viewNavigations: Record<ViewType, NavigationItem[]> = {
  dashboard: [
    { name: 'Dashboard', href: '/', icon: Home, current: true, moduleName: 'Dashboard' },
    { name: 'Businesses', href: '/businesses', icon: Building, current: false, moduleName: 'Business' },
    { name: 'Users', href: '/users', icon: User, current: false, moduleName: 'Users' },
  ],
  accounting: [
    { name: 'Overview', href: '/', icon: BarChart4, current: true, moduleName: 'Accounting' },
    { 
      name: 'Documents', 
      href: '#', 
      icon: FileText,
      current: false, 
      moduleName: 'Documents',
      subNavigation: [
        { name: 'Quotations', href: '/quotations', current: false, moduleName: 'Quotations' },
        { name: 'Invoices', href: '/invoices', current: false, moduleName: 'Invoices' },
        { name: 'Receipts', href: '/receipts', current: false, moduleName: 'Receipts' },
        { name: 'Bills', href: '/bills', current: false, moduleName: 'Bills' },
        { name: 'Suppliers', href: '/suppliers', current: false, moduleName: 'Suppliers' },
        { name: 'Product & Services', href: '/product-services', current: false, moduleName: 'Products' },
      ] 
    },
    { name: 'Chart of Accounts', href: '/chart-of-accounts', icon: CreditCard, current: false, moduleName: 'Accounting' },
    { name: 'Transactions', href: '/transactions', icon: DollarSign, current: false, moduleName: 'Accounting' },
    { name: 'Reports', href: '/reports', icon: BarChart4, current: false, moduleName: 'Reports' },
    { 
      name: 'AI Processing', 
      href: '#', 
      icon: FileText,
      current: false, 
      moduleName: 'Documents',
      subNavigation: [
        { name: 'Document Types', href: '/document-types', current: false, moduleName: 'Documents' },
        { name: 'Document Templates', href: '/document-templates', current: false, moduleName: 'Documents' },
        { name: 'Document Uploads', href: '/document-uploads', current: false, moduleName: 'Documents' },
        { name: 'Exception Queue', href: '/exception-queue', current: false, moduleName: 'Documents' },
        { name: 'Approval Workflows', href: '/approval-workflows', current: false, moduleName: 'Documents' },
        { name: 'Approval Requests', href: '/approval-requests', current: false, moduleName: 'Documents' },
        { name: 'Processing Queue', href: '/processing-queue', current: false, moduleName: 'Documents' },
        { name: 'AI Configuration', href: '/ai-configuration', current: false, moduleName: 'Documents' },
        { name: 'Document Processing Reports', href: '/document-processing-reports', current: false, moduleName: 'Documents' },
        { name: 'Document Processing Settings', href: '/document-processing-settings', current: false, moduleName: 'Documents' },
      ] 
    },
  ],
  sales: [
    { name: 'Sales Dashboard', href: '/', icon: LineChart, current: true, moduleName: 'Sales' },
    { name: 'Deals', href: '/deals', icon: Briefcase, current: false, moduleName: 'Deals' },
    { name: 'Sales Pipeline', href: '/sales-pipeline', icon: BarChart4, current: false, moduleName: 'Sales' },
    { name: 'Products & Services', href: '/product-services', icon: Package, current: false, moduleName: 'Products' },
    { name: 'Sales Reports', href: '/sales-reports', icon: BarChart4, current: false, comingSoon: true, moduleName: 'Sales' },
  ],
  crm: [
    { name: 'CRM Dashboard', href: '/', icon: Users, current: true, moduleName: 'CRM' },
    { name: 'Customers', href: '/customers', icon: Users, current: false, moduleName: 'Customers' },
    { name: 'Companies', href: '/companies', icon: Building, current: false, moduleName: 'Contacts' },
    { name: 'Campaigns', href: '/campaigns', icon: ReceiptText, current: false, moduleName: 'Campaigns' },
    { name: 'Calendar', href: '/calendar', icon: Calendar, current: false, moduleName: 'Calendar' },
    { name: 'Communications', href: '/communications', icon: Phone, current: false, comingSoon: true, moduleName: 'CRM' },
    { name: 'Signatures', href: '/signatures', icon: PenSquare, current: false, moduleName: 'CRM' },
  ],
  inventory: [
    { name: 'Inventory Dashboard', href: '/', icon: Warehouse, current: true, moduleName: 'Inventory' },
    { name: 'Items', href: '/inventory/items', icon: Package, current: false, moduleName: 'Inventory' },
    { name: 'Categories', href: '/inventory/categories', icon: FileText, current: false, moduleName: 'Inventory' },
    { name: 'Stock Levels', href: '/inventory/stock', icon: BarChart4, current: false, moduleName: 'Inventory' },
    { name: 'Stock Movements', href: '/inventory/movements', icon: Truck, current: false, moduleName: 'Inventory' },
    { name: 'Reorder Alerts', href: '/inventory/reorder-alerts', icon: Bell, current: false, moduleName: 'Inventory' },
    { name: 'Warehouses', href: '/warehouses', icon: Building, current: false, moduleName: 'Inventory' },
    { name: 'Stock Transfers', href: '/stock-transfers', icon: Truck, current: false, moduleName: 'Inventory' },
    { name: 'Inventory Reports', href: '/inventory/reports', icon: BarChart3, current: false, moduleName: 'Inventory' },
  ],
  manufacturing: [
    { name: 'Manufacturing Dashboard', href: '/', icon: Factory, current: true, moduleName: 'Manufacturing' },
    { name: 'Recipes (BOM)', href: '/manufacturing/recipes', icon: ClipboardList, current: false, moduleName: 'Manufacturing' },
    { name: 'Production Jobs', href: '/manufacturing/production-jobs', icon: Factory, current: false, moduleName: 'Manufacturing' },
    { name: 'Work Orders', href: '/manufacturing/work-orders', icon: ClipboardList, current: false, moduleName: 'Manufacturing' },
    { name: 'Production Schedule', href: '/manufacturing/schedule', icon: Calendar, current: false, moduleName: 'Manufacturing' },
    { name: 'Quality Control', href: '/manufacturing/quality-control', icon: Search, current: false, moduleName: 'Manufacturing' },
    { name: 'Production Reports', href: '/manufacturing/reports', icon: BarChart3, current: false, moduleName: 'Manufacturing' },
  ],
  procurement: [
    { name: 'Procurement Dashboard', href: '/', icon: ShoppingCart, current: true, moduleName: 'Procurement' },
    { 
      name: 'Requisitions', 
      href: '#', 
      icon: ClipboardList,
      current: false, 
      moduleName: 'Procurement',
      subNavigation: [
        { name: 'Purchase Requisitions', href: '/procurement/requisitions', current: false, moduleName: 'Procurement' },
        { name: 'Pending Approvals', href: '/procurement/pending-approvals', current: false, moduleName: 'Procurement' },
      ] 
    },
    { 
      name: 'Purchase Orders', 
      href: '#', 
      icon: FileText,
      current: false, 
      moduleName: 'Procurement',
      subNavigation: [
        { name: 'Purchase Orders', href: '/procurement/purchase-orders', current: false, moduleName: 'Procurement' },
        { name: 'Supplier Quotations', href: '/procurement/quotations', current: false, moduleName: 'Procurement' },
      ] 
    },
    { 
      name: 'Goods Receipt', 
      href: '#', 
      icon: Truck,
      current: false, 
      moduleName: 'Procurement',
      subNavigation: [
        { name: 'Goods Received Vouchers', href: '/procurement/goods-received-vouchers', current: false, moduleName: 'Procurement' },
        { name: 'Parked Goods', href: '/procurement/parked-goods', current: false, moduleName: 'Procurement' },
      ] 
    },
    { name: 'Supplier Mall', href: '/procurement/supplier-mall', icon: Building, current: false, moduleName: 'Procurement' },
    { name: 'Budget Management', href: '/procurement/budget', icon: DollarSign, current: false, moduleName: 'Procurement' },
    { name: 'Procurement Reports', href: '/procurement/reports', icon: BarChart3, current: false, moduleName: 'Procurement' },
  ],
};

// Get view color class
const getViewColorClass = (viewId: string): string => {
  const view = views.find(v => v.id === viewId);
  return view ? view.color : '';
};

const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
const user = {
  name: storedUser?.firstName + ' ' + storedUser?.lastName || 'User Name',
  email: storedUser?.emailAddress || 'user@example.com',
  imageUrl: storedUser?.profileImg || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
};

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const brand = useBrand();
  const [open, setOpen] = useState(false);
  const [businesses] = useState(() => {
    const storedBusinesses = typeof window !== 'undefined' ? window.localStorage.getItem('businesses') : null;
    return storedBusinesses ? JSON.parse(storedBusinesses) : [];
  });
  
  const [selectedBusiness, setSelectedBusiness] = useState(() => {
    const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
    return storedBusiness ? JSON.parse(storedBusiness) : null;
  });

  // Get the current view from localStorage or default to dashboard
  const [currentView, setCurrentView] = useState<ViewType>(() => {
    const storedView = typeof window !== 'undefined' ? window.localStorage.getItem('currentView') : null;
    return (storedView || 'dashboard') as ViewType;
  });

  // Get navigation based on current view
  const navigation = viewNavigations[currentView];
  
  const toggleMenu = () => {
    setOpen(!open);
  };

  const handleBusinessSelect = (business: any) => {
    setSelectedBusiness(business);
    typeof window !== 'undefined' ? window.localStorage.setItem('selectedBusiness', JSON.stringify(business)) : null;
    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  // Handle view change
  const handleViewChange = (viewId: ViewType) => {
    setCurrentView(viewId);
    localStorage.setItem('currentView', viewId);
    
    // Navigate to the main page of the selected view
    //const mainNavItem = viewNavigations[viewId][0];
    //navigate(mainNavItem.href);
    window.location.reload();
  };

  // Check if access token exists, otherwise redirect to login
  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken && location.pathname !== '/login' && location.pathname !== '/signup') {
      localStorage.removeItem('user');
      localStorage.removeItem('businesses');
      localStorage.removeItem('selectedBusiness');
      localStorage.removeItem('currentView');
      localStorage.removeItem('modulePermissions');
      window.location.href = '/login';
    } else {
      // Validate that module permissions exist
      validatePermissions();
      // Initialize missing modules with default permissions
      initializeMissingModules();
    }
  }, [location.pathname]);

  // Get current view color
  const currentViewColor = getViewColorClass(currentView);

  // Handle navigation for disabled items
  const handleDisabledLink = (e: React.MouseEvent, comingSoon: boolean) => {
    if (comingSoon) {
      e.preventDefault();
    }
  };

  // Filter views based on permissions
  const availableViews = views.filter(view => canView(view.moduleName || ''));

  // Filter navigation items based on permissions
  const filteredNavigation = navigation.filter(item => {
    // Check main navigation item permission
    if (!canView(item.moduleName || '')) {
      return false;
    }
    
    // If it has sub-navigation, filter sub-items too
    if (item.subNavigation) {
      item.subNavigation = item.subNavigation.filter(subItem => 
        canView(subItem.moduleName || '')
      );
      
      // Only show if there are visible sub-items
      return item.subNavigation.length > 0;
    }
    
    return true;
  });

  // Get brand logo icon
  const getBrandIcon = () => {
    // Check if brand has a custom logo URL first
    if (brand.logo.url) {
      return <img src={brand.logo.url} alt={brand.name} className="w-10 h-10 object-contain" style={{ width: '100px', height: '100px' }} />;
    }
    
    if (brand.logo.icon === 'SparklesIcon') {
      return <Sparkles className="w-8 h-8" />;
    } else if (brand.logo.icon === 'BoxIcon') {
      return <Box className="w-8 h-8" />;
    } else if (brand.logo.icon === 'HomeIcon') {
      return <Home className="w-8 h-8" />;
    }
    return <img src={Favicon} alt={brand.name} className="w-8 h-8" />;
  };

  return (
    <>
      <header className="bg-white border-b border-border/40 sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <div className="flex items-center">
              <Link to="/" className="flex items-center gap-2">
                {getBrandIcon()}
              </Link>
            </div>

            {/* View Switcher (Desktop) */}
            <div className="hidden lg:block flex-1 max-w-2xl mx-4">
              <div className="bg-slate-50 p-1 rounded-lg">
                <div className="flex items-center justify-center space-x-1 overflow-x-auto navbar-view-switcher">
                  {availableViews.map((view) => (
                    <button
                      key={view.id}
                      onClick={() => handleViewChange(view.id as ViewType)}
                      className={cn(
                        "flex items-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap flex-shrink-0",
                        currentView === view.id 
                          ? getViewColorClass(view.id) 
                          : "text-muted-foreground hover:bg-slate-100"
                      )}
                    >
                      <view.icon className="w-3 h-3" />
                      <span className="hidden xl:inline">{view.name}</span>
                      <span className="xl:hidden">{view.name.split(' ')[0]}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Menu Items */}
            <div className="flex items-center gap-3">
              {/* Business Selector */}
              {businesses.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="hidden sm:flex">
                      {selectedBusiness?.name || 'Select Business'}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>Your Businesses</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    {businesses.map((business: any) => (
                      <DropdownMenuItem 
                        key={business.id} 
                        className={selectedBusiness?.id === business.id ? 'bg-secondary' : ''}
                        onClick={() => handleBusinessSelect(business)}
                      >
                        {business.name}
                      </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    {canCreate('Business') && (
                      <DropdownMenuItem asChild>
                        <Link to="/businesses/create" className="flex items-center gap-2">
                          <Plus className="w-4 h-4" />
                          <span>Create New Business</span>
                        </Link>
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full overflow-hidden">
                    <img
                      src={user.imageUrl}
                      alt={user.name}
                      className="w-8 h-8 rounded-full object-cover"
                    />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">{user.name}</p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {user.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile">Profile</Link>
                  </DropdownMenuItem>
                  {canView('Settings') && (
                    <DropdownMenuItem asChild>
                      <Link to="/settings">Settings</Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/logout">Logout</Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={toggleMenu}
              >
                {open ? <X /> : <Menu />}
              </Button>
            </div>
          </div>
        </div>

        {/* View-Specific Navigation Bar */}
        <div className={cn(
          "border-t border-border/40 py-1 px-4 transition-all",
          currentViewColor.split(' ')[0].replace('bg-', 'bg-opacity-30 bg-')
        )}>
          <div className="container mx-auto">
            <nav className="hidden lg:flex items-center space-x-1 overflow-x-auto navbar-view-switcher">
              {filteredNavigation.map((item) => (
                <div key={item.name} className="relative">
                  {item.subNavigation ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button 
                          variant="ghost" 
                          className={cn(
                            "flex items-center gap-1 px-3 py-1.5 text-sm font-medium whitespace-nowrap",
                            location.pathname.startsWith(item.href.split('?')[0]) && 
                            "bg-white/50 shadow-sm"
                          )}
                        >
                          <item.icon className="w-4 h-4 mr-1" />
                          {item.name} <ChevronDown className="w-3 h-3 ml-1" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="center" className="w-48">
                        <DropdownMenuLabel>{item.name}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {item.subNavigation.map((subItem) => (
                          <DropdownMenuItem key={subItem.name} asChild>
                            <Link to={subItem.href} className="w-full">
                              {subItem.name}
                            </Link>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  ) : (
                    <Link
                      to={item.comingSoon ? "#" : item.href}
                      onClick={(e) => handleDisabledLink(e, !!item.comingSoon)}
                      className={cn(
                        "flex items-center gap-1 px-3 py-1.5 rounded-md text-sm font-medium whitespace-nowrap",
                        location.pathname === item.href
                          ? "bg-white/50 shadow-sm"
                          : "hover:bg-white/30",
                        item.comingSoon && "opacity-60 cursor-not-allowed"
                      )}
                    >
                      <item.icon className="w-4 h-4 mr-1" />
                      {item.name}
                      {item.comingSoon && (
                        <span className="ml-1 text-xs font-medium bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                          Soon
                        </span>
                      )}
                    </Link>
                  )}
                </div>
              ))}
            </nav>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {open && (
          <div className="lg:hidden border-t border-border/40">
            <div className="space-y-1 px-4 py-3">
              {/* View Switcher (Mobile) */}
              <div className="mb-4">
                <h3 className="text-sm font-medium text-foreground mb-2">Switch View</h3>
                <div className="grid grid-cols-2 gap-2">
                  {availableViews.map((view) => (
                    <Button
                      key={view.id}
                      size="sm"
                      className={cn(
                        "flex items-center justify-center gap-1 text-xs",
                        currentView === view.id ? getViewColorClass(view.id) : "bg-slate-50"
                      )}
                      onClick={() => {
                        handleViewChange(view.id as ViewType);
                        toggleMenu();
                      }}
                    >
                      <view.icon className="w-3 h-3" />
                      <span className="truncate">{view.name.split(' ')[0]}</span>
                    </Button>
                  ))}
                </div>
              </div>
              
              <div className="pt-2 border-t border-border/40">
                <h3 className="text-sm font-medium text-foreground mb-2">{views.find(v => v.id === currentView)?.name} Navigation</h3>
                {filteredNavigation.map((item) => (
                  <div key={item.name} className="py-1">
                    {item.subNavigation ? (
                      <div className="space-y-1">
                        <div className="flex items-center px-3 py-2 text-base font-medium text-foreground">
                          <item.icon className="w-4 h-4 mr-2" />
                          {item.name}
                        </div>
                        <div className="pl-4 space-y-1 border-l border-border/60">
                          {item.subNavigation.map((subItem) => (
                            <Link
                              key={subItem.name}
                              to={subItem.href}
                              className="block px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
                              onClick={toggleMenu}
                            >
                              {subItem.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        to={item.comingSoon ? "#" : item.href}
                        onClick={(e) => {
                          handleDisabledLink(e, !!item.comingSoon);
                          if (!item.comingSoon) toggleMenu();
                        }}
                        className={cn(
                          "flex items-center px-3 py-2 text-base font-medium",
                          location.pathname === item.href
                            ? 'text-primary'
                            : 'text-muted-foreground hover:text-foreground',
                          item.comingSoon && "opacity-60 cursor-not-allowed"
                        )}
                      >
                        <item.icon className="w-4 h-4 mr-2" />
                        {item.name}
                        {item.comingSoon && (
                          <span className="ml-auto text-xs font-medium bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                            Coming Soon
                          </span>
                        )}
                      </Link>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Business Selector in Mobile Menu */}
              {businesses.length > 0 && (
                <div className="py-3 px-3 border-t border-border/40 mt-2">
                  <div className="text-base font-medium mb-2">Your Businesses</div>
                  <div className="space-y-1">
                    {businesses.map((business: any) => (
                      <button
                        key={business.id}
                        className={`block w-full text-left px-3 py-2 text-sm ${
                          selectedBusiness?.id === business.id 
                            ? 'bg-secondary text-secondary-foreground rounded-md' 
                            : 'text-muted-foreground'
                        }`}
                        onClick={() => {
                          handleBusinessSelect(business);
                          toggleMenu();
                        }}
                      >
                        {business.name}
                      </button>
                    ))}
                    {canCreate('Business') && (
                      <Link
                        to="/businesses/create"
                        className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary"
                        onClick={toggleMenu}
                      >
                        <Plus className="w-4 h-4" />
                        <span>Create New Business</span>
                      </Link>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Mobile Bottom Navigation */}
      <div className="mobile-nav">
        {availableViews.slice(0, 5).map((view) => (
          <Button
            key={view.id}
            variant="ghost"
            className={cn(
              "flex flex-col items-center justify-center p-1 rounded-md min-w-0 flex-1",
              currentView === view.id
                ? "text-primary"
                : "text-muted-foreground"
            )}
            onClick={() => handleViewChange(view.id as ViewType)}
          >
            <view.icon className="w-4 h-4" />
            <span className="text-xs mt-1 truncate">{view.name.split(' ')[0]}</span>
          </Button>
        ))}
        {availableViews.length > 5 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex flex-col items-center justify-center p-1 rounded-md min-w-0 flex-1 text-muted-foreground"
              >
                <Menu className="w-4 h-4" />
                <span className="text-xs mt-1">More</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuLabel>More Views</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {availableViews.slice(5).map((view) => (
                <DropdownMenuItem
                  key={view.id}
                  onClick={() => handleViewChange(view.id as ViewType)}
                  className={cn(
                    "flex items-center gap-2",
                    currentView === view.id && "bg-secondary"
                  )}
                >
                  <view.icon className="w-4 h-4" />
                  {view.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </>
  );
}